/**
 * Skeleton — the only loading affordance in the product. A spinner where a
 * skeleton belongs is an automatic reject, so this has to be easy to reach for.
 *
 * Sizes are explicit on purpose: a skeleton must match the geometry of the thing
 * it stands in for, or the layout jumps when the data lands.
 */

import type { CSSProperties, HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Extent = number | string;

const RADIUS: Record<'none' | 'xs' | 'sm' | 'md' | 'lg' | 'full', string> = {
  none: '0',
  xs: 'var(--radius-xs)',
  sm: 'var(--radius-sm)',
  md: 'var(--radius-md)',
  lg: 'var(--radius-lg)',
  full: 'var(--radius-full)',
};

function extent(value: Extent): string {
  return typeof value === 'number' ? `${value}px` : value;
}

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Number → px. String → used verbatim (`100%`, `12ch`, `var(--w-detail)`). */
  w?: Extent;
  h?: Extent;
  rounded?: keyof typeof RADIUS;
  /** More than one renders a paragraph block with a shortened last line. */
  lines?: number;
  /** Width of the final line in a multi-line block. */
  lastLineWidth?: string;
}

export function Skeleton({
  w = '100%',
  h = 12,
  rounded = 'sm',
  lines = 1,
  lastLineWidth = '62%',
  className,
  style,
  ...rest
}: SkeletonProps) {
  const base: CSSProperties = {
    width: extent(w),
    height: extent(h),
    borderRadius: RADIUS[rounded],
    ...style,
  };

  if (lines <= 1) {
    return <div aria-hidden="true" className={cn('skeleton', className)} style={base} {...rest} />;
  }

  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-2', className)} {...rest}>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton"
          style={{
            ...base,
            width: index === lines - 1 ? lastLineWidth : base.width,
          }}
        />
      ))}
    </div>
  );
}
